import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { List, ListItem, Paper, Grid } from '@material-ui/core';
import { useSortableData } from '../../hooks/objective/useSortableData';
import ObjectiveItem from './ObjectiveItem/ObjectiveItem';
import LoadMoreButton from './LoadMoreButton';
import ArrowDownwardRoundedIcon from '@material-ui/icons/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@material-ui/icons/ArrowUpwardRounded';
import clsx from 'clsx';

const useStyles = makeStyles (theme => ({
  root: {
    margin: 16,
    verticalAlign: 'middle'
  },
  header: {
    display: 'grid',
    width: '100%',
    gridGap: 8,
    alignItems: 'center',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gridTemplateAreas: `
      'name     name     actions  actions'
      'divider1 divider1 divider1   divider1'
      'category deadline weight     mean'
      'divider2 divider2 divider2   divider2'
      'status   status   rating     rating'
      `,
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '1fr 1fr 75px 65px 1fr 1fr 65px 96px',
      gridTemplateAreas: `
        'name category deadline weight status rating mean actions'
      `
    }
  },
  headerButton: {
    color: '#999999',
    justifyContent: 'flex-start',
    minWidth: 0,
    height: 'unset',
    fontSize: 11,
    marginLeft: -8, //Keep padding on header buttons, while still being in line with the data
    '& svg': {
      height: 20,
      width: 20
    },
    '& p': {
      margin: 0
    },
    '&.Mui-disabled': {
      color: '#999999'
    },
    '& .MuiButton-label': {
      height: 20
    }
  },
  list: {
    padding: 0
  },
  listItem: {
    padding: 0,
    marginBottom: 16,
    [theme.breakpoints.up('md')]: {
      marginBottom: 0
    }
  },
  groupColumns: {
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gridTemplateAreas: `
      'name     name     actions    actions'
      'divider1 divider1 divider1   divider1'
      'category deadline weight     mean'
      'divider2 divider2 divider2   divider2'
      'status   status   rating     rating'
      'divider3 divider3 divider3   divider3'
      'group    group    appraiser  appraiser'
      `,
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '1fr 1fr 1fr 1fr 75px 65px 1fr 1fr 65px 96px',
      gridTemplateAreas: `
        'name category group appraiser deadline weight status rating mean actions'
      `
    }
  }
}), { name: 'ObjectiveList' });

const ObjectiveList = ({
  objectives,
  loading,
  onSubmitSuccess,
  canLoadMore,
  loadMore,
  showGroupColumns
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up('md'));

  const { items, requestSort, sortConfig } = useSortableData(objectives);

  const arrowButton = {
    ascending: <ArrowDownwardRoundedIcon/>,
    descending: <ArrowUpwardRoundedIcon/>
  };

  const getClassNamesFor = name => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === name ? arrowButton[sortConfig.direction] : '';
  };

  return (
    <div>
      <Paper className={ classes.root }>
        {md &&
          <Box px={ 2 } borderBottom={ '1px solid #DFE0EB' }>
            <div className={ clsx(classes.header, {
              [classes.groupColumns]: showGroupColumns
            }) }>
              <Button
                onClick={ () => requestSort('name') }
                style={ { gridArea: 'name' } }
                className={ classes.headerButton }
              >
                <p>{t('objective.listHeader.name')}</p>
                {getClassNamesFor('name')}
              </Button>
              <Button
                onClick={ () => requestSort('category') }
                style={ { gridArea: 'category' } }
                className={ classes.headerButton }
              >
                <p>{t('objective.listHeader.category')}</p>
                {getClassNamesFor('category')}
              </Button>
              {showGroupColumns &&
                <>
                  <Button
                    // onClick={ () => requestSort('appraiser') }
                    style={ { gridArea: 'group' } }
                    className={ classes.headerButton }
                    disabled
                  >
                    <p>{t('objective.listHeader.group')}</p>
                    {getClassNamesFor('appraiser')}
                  </Button>
                  <Button
                    // onClick={ () => requestSort('appraiser') }
                    style={ { gridArea: 'appraiser' } }
                    className={ classes.headerButton }
                    disabled
                  >
                    <p>{t('objective.listHeader.appraiser')}</p>
                    {getClassNamesFor('appraiser')}
                  </Button>
                </>
              }
              <Button
                onClick={ () => requestSort('deadline') }
                style={ { gridArea: 'deadline' } }
                className={ classes.headerButton }
              >
                <p>{t('objective.listHeader.deadline')}</p>
                {getClassNamesFor('deadline')}
              </Button>
              <Button
                onClick={ () => requestSort('weight') }
                style={ { gridArea: 'weight' } }
                className={ classes.headerButton }
              >
                <p>{t('objective.listHeader.weight')}</p>
                {getClassNamesFor('weight')}
              </Button>
              <Button
                onClick={ () => requestSort('status') }
                style={ { gridArea: 'status' } }
                className={ classes.headerButton }
              >
                <p>{t('objective.listHeader.status')}</p>
                {getClassNamesFor('status')}
              </Button>
              <Button
                // onClick={() => requestSort('')}
                style={ { gridArea: 'rating' } }
                className={ classes.headerButton }
                disabled
              >
                <p>{t('objective.listHeader.rating')}</p>
              </Button>
              <Button
                // onClick={() => requestSort('')}
                style={ { gridArea: 'mean' } }
                className={ classes.headerButton }
                disabled
              >
                <p>{t('objective.listHeader.result')}</p>
              </Button>
              <div style={ { gridArea: 'actions' } } />
            </div>
          </Box>
        }
        <List className={ classes.list }>
          {items.map(objective => (
            <ListItem key={ objective.objective_id } className={ classes.listItem }>
              <ObjectiveItem
                key={ objective.objective_id }
                objective={ objective }
                onSubmitSuccess={ onSubmitSuccess }
              />
            </ListItem>
          ))}
        </List>
      </Paper>
      <Grid container justifyContent="center">
        <LoadMoreButton
          loading={ loading }
          canLoadMore={ canLoadMore }
          loadMore={ loadMore }
          loadMoreTranslation={ t('objectiveList.loadMoreBtn') }
          noMoreToLoadTranslation={ t('objectiveList.NoMoreObjectives') }
        />
      </Grid>
    </div>
  );
};

export default ObjectiveList;
